import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchBuiltInDraft, type TranslatableDocument } from '../../i18n/documents';
import { getDocument, i18nErrorMessage, saveDocument, type ServerDocument } from '../../services/i18nService';
import { loadEnglishDocument, missingDocumentTags, type DocumentRow } from '../../utils/documentRows';
import { ROW_STATE_LABELS } from '../../utils/translationRows';
import { useDialog } from '../../context/DialogContext';
import styles from '../../styles/translationAdmin.module.css';

interface DocumentReviewProps {
  row: DocumentRow;
  languageCode: string;
  languageName: string;
  onBack: () => void;
  onSaved: (doc: TranslatableDocument, saved: ServerDocument | null) => void;
}

function formatWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Show the app's section tags as visible markers instead of raw text. */
function previewMarkdown(text: string): string {
  return text
    .replace(/<<title:\s*(.*?)>>/g, '# $1')
    .replace(/<<subtitle:\s*(.*?)>>/g, '*$1*')
    .replace(/<<([^<>]+?)>>/g, '`⟨$1⟩`');
}

function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const DocumentReview: React.FC<DocumentReviewProps> = ({ row, languageCode, languageName, onBack, onSaved }) => {
  const { doc } = row;
  const [english, setEnglish] = useState<string | null>(null);
  const [saved, setSaved] = useState<ServerDocument | null>(null);
  const [builtIn, setBuiltIn] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { confirmAsync } = useDialog();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [en, server, draftText] = await Promise.all([
          loadEnglishDocument(doc),
          row.saved ? getDocument(languageCode, doc.id) : Promise.resolve(null),
          fetchBuiltInDraft(doc, languageCode),
        ]);
        if (cancelled) return;
        setEnglish(en);
        setSaved(server);
        setBuiltIn(draftText);
      } catch (e) {
        if (!cancelled) setLoadError(i18nErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Reload only when switching documents; row.saved changes after our own saves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id, languageCode]);

  const current = saved?.text || builtIn || '';
  const shown = editing ? draft : current;
  const missingTags = useMemo(
    () => (english && shown ? missingDocumentTags(english, shown) : []),
    [english, shown]
  );
  const dirty = editing && draft !== current;

  const persist = async (text: string, status: 'draft' | 'approved', success: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await saveDocument(languageCode, doc.id, { text, status, source_hash: row.englishHash });
      setSaved(result);
      setEditing(false);
      setUploadedName(null);
      setNotice(success);
      onSaved(doc, result);
    } catch (e) {
      setError(i18nErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const startEditing = (text: string = current) => {
    setDraft(text);
    setEditing(true);
    setNotice(null);
  };

  const handleUpload = async (file: File) => {
    const text = await file.text();
    setUploadedName(file.name);
    startEditing(text);
  };

  const handleRemove = async () => {
    const fallback = builtIn ? 'the built-in AI draft' : 'English';
    const ok = await confirmAsync({
      title: 'Remove saved version?',
      message: `Members will see ${fallback} instead of the saved ${languageName} version. This can't be undone.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (ok) void persist('', 'draft', `Saved version removed — showing ${fallback}.`);
  };

  const handleCancelEdit = async () => {
    if (dirty) {
      const ok = await confirmAsync({
        title: 'Discard your changes?',
        message: 'Your edits to this document haven’t been saved.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
        variant: 'warning',
      });
      if (!ok) return;
    }
    setEditing(false);
    setUploadedName(null);
  };

  if (loading) return <p className={styles.muted}>Loading document…</p>;
  if (loadError || english === null) {
    return (
      <div className={styles.card}>
        <p className={styles.error}>{loadError || 'Couldn’t load this document.'}</p>
        <button type="button" className={styles.linkButton} onClick={onBack}>
          ← Back
        </button>
      </div>
    );
  }

  const canApprove = !editing && Boolean(current) && row.state !== 'approved' && missingTags.length === 0;
  const fileBase = doc.file.replace(/\.md$/, '');

  return (
    <div>
      <div className={styles.reviewHeader}>
        <button type="button" className={styles.linkButton} onClick={onBack}>
          ← {languageName} overview
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.reviewHeader}>
          <h2 style={{ margin: 0 }}>{doc.title}</h2>
          <span className={styles[`badge_${row.state}`]}>{ROW_STATE_LABELS[row.state]}</span>
        </div>
        <p className={styles.muted}>
          {saved
            ? `Saved ${formatWhen(saved.updated_at)}${saved.updated_by ? ` by ${saved.updated_by}` : ''}.` +
              (saved.status === 'approved' && saved.approved_at
                ? ` Approved ${formatWhen(saved.approved_at)}${saved.approved_by ? ` by ${saved.approved_by}` : ''}.`
                : '')
            : builtIn
              ? 'Showing the AI draft that ships with the app. Approve it as-is, or edit it first.'
              : `No ${languageName} version yet — members see English. Upload a translation or write one.`}
        </p>
        {row.state === 'stale' && (
          <p className={styles.warning}>
            The English changed after this was saved. Compare both sides, update the translation, then approve again.
          </p>
        )}

        {error && <div className={styles.error}>{error}</div>}
        {notice && <div className={styles.success}>{notice}</div>}

        <div className={styles.actions}>
          {!editing && (
            <>
              <button
                type="button"
                className={styles.button}
                disabled={busy || !canApprove}
                onClick={() => void persist(current, 'approved', 'Approved.')}
              >
                Approve
              </button>
              <button type="button" className={styles.buttonSecondary} disabled={busy} onClick={() => startEditing()}>
                {current ? 'Edit' : 'Write translation'}
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                className={styles.button}
                disabled={busy || !draft.trim() || missingTags.length > 0}
                onClick={() => void persist(draft, 'approved', 'Saved and approved.')}
              >
                Save &amp; approve
              </button>
              <button
                type="button"
                className={styles.buttonSecondary}
                disabled={busy || !draft.trim()}
                onClick={() => void persist(draft, 'draft', 'Saved as a draft (not approved yet).')}
              >
                Save draft
              </button>
              <button
                type="button"
                className={styles.buttonSecondary}
                disabled={busy}
                onClick={() => void handleCancelEdit()}
              >
                Cancel
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.buttonSecondary}
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            Upload .md
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".md,.txt,text/markdown,text/plain"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleUpload(file);
            }}
          />
          <button
            type="button"
            className={styles.buttonSecondary}
            disabled={!shown}
            onClick={() => downloadText(`${fileBase}.${languageCode}.md`, shown)}
          >
            Download {languageName}
          </button>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={() => downloadText(doc.file, english)}
          >
            Download English
          </button>
          {saved && !editing && (
            <button type="button" className={styles.buttonDanger} disabled={busy} onClick={() => void handleRemove()}>
              Remove saved version
            </button>
          )}
        </div>

        {uploadedName && (
          <p className={styles.muted}>Loaded {uploadedName} — not saved yet. Review it, then save.</p>
        )}
        {!canApprove && !editing && current && row.state !== 'approved' && missingTags.length > 0 && (
          <p className={styles.muted}>Fix the missing tags before approving.</p>
        )}
        {missingTags.length > 0 && (
          <div className={styles.warning}>
            The app uses these tags to find sections of this document. Copy them exactly from the English:
            <ul>
              {missingTags.map((tag) => (
                <li key={tag}>
                  <code>{tag}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={styles.docColumns}>
        <div className={styles.docPane}>
          <div className={styles.docPaneTitle}>English</div>
          {editing ? (
            <pre className={styles.docRaw}>{english}</pre>
          ) : (
            <div className={styles.docPreview}>
              <ReactMarkdown>{previewMarkdown(english)}</ReactMarkdown>
            </div>
          )}
        </div>
        <div className={styles.docPane}>
          <div className={styles.docPaneTitle}>
            {languageName}
            {editing ? ' — markdown' : ''}
          </div>
          {editing ? (
            <textarea
              className={dirty ? styles.textareaDirty : styles.textarea}
              style={{ minHeight: '70vh', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem' }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck
              lang={languageCode}
            />
          ) : current ? (
            <div className={styles.docPreview} lang={languageCode}>
              <ReactMarkdown>{previewMarkdown(current)}</ReactMarkdown>
            </div>
          ) : (
            <p className={styles.muted}>Nothing yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentReview;
